import React, { useEffect, useRef, useState } from 'react';

/**
 * AnimatedCounter Component
 * Displays a number that counts up from 0 to the target value with animation
 * @param {number|string} value - The target value to count to
 * @param {number} duration - Duration of the animation in milliseconds (default: 3000)
 * @param {string} className - Additional CSS classes for styling
 * @param {string} easing - Easing function for the animation (default: 'easeOut')
 * @param {boolean} autoStart - Whether to start animation automatically when component mounts
 * @param {boolean} formatNumber - Whether to format the number with commas
 * @param {string} prefix - Text to display before the number
 * @param {string} suffix - Text to display after the number
 * @param {number} minDuration - Minimum duration regardless of value size (default: 1500)
 * @param {number} maxDuration - Maximum duration regardless of value size (default: 5000)
 */
const AnimatedCounter = ({ 
  value, 
  duration = 3000, // Increased default duration from 2000 to 3000
  className = '', 
  easing = 'easeOut',
  autoStart = false,
  formatNumber = true,
  prefix = '',
  suffix = '',
  minDuration = 1500, // Minimum duration in ms
  maxDuration = 5000  // Maximum duration in ms
}) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(autoStart);
  const [actualDuration, setActualDuration] = useState(duration);
  const counterRef = useRef(null);
  
  // Ensure value is a number (handle string values with commas)
  const numericValue = React.useMemo(() => {
    if (value === null || value === undefined) return 0;
    
    if (typeof value === 'string') {
      // Remove commas and any non-numeric characters except decimal point
      const cleanedValue = value.replace(/[^\d.]/g, '');
      const parsedValue = parseFloat(cleanedValue);
      return isNaN(parsedValue) ? 0 : parsedValue;
    }
    
    return Number(value);
  }, [value]);
  
  // Calculate duration based on value size
  useEffect(() => {
    if (numericValue <= 0) return;
    
    // Calculate duration based on the value (larger values get longer durations)
    let calculatedDuration = duration;
    
    if (numericValue > 1000) {
      calculatedDuration = Math.max(minDuration, Math.min(maxDuration, numericValue * 1.5));
    } else if (numericValue > 100) {
      calculatedDuration = Math.max(minDuration, Math.min(maxDuration, numericValue * 3));
    } else {
      calculatedDuration = Math.max(minDuration, Math.min(maxDuration, numericValue * 10));
    }
    
    setActualDuration(calculatedDuration);
  }, [numericValue, duration, minDuration, maxDuration]);
  
  // Easing functions
  const easingFunctions = {
    linear: t => t,
    easeIn: t => t * t,
    easeOut: t => t * (2 - t),
    easeInOut: t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,
    bounce: t => {
      if (t < 1 / 2.75) {
        return 7.5625 * t * t;
      } else if (t < 2 / 2.75) {
        return 7.5625 * (t -= 1.5 / 2.75) * t + 0.75;
      } else if (t < 2.5 / 2.75) {
        return 7.5625 * (t -= 2.25 / 2.75) * t + 0.9375;
      } else {
        return 7.5625 * (t -= 2.625 / 2.75) * t + 0.984375;
      }
    }
  };

  useEffect(() => {
    if (autoStart) {
      setIsVisible(true);
      return;
    }

    // Set up Intersection Observer to trigger animation when element is in viewport
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          // Stop observing after animation is triggered
          if (counterRef.current) {
            observer.unobserve(counterRef.current);
          }
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    if (counterRef.current) {
      observer.observe(counterRef.current);
    }

    return () => {
      if (counterRef.current) {
        observer.unobserve(counterRef.current);
      }
    };
  }, [autoStart]);

  useEffect(() => {
    if (!isVisible || numericValue <= 0) return;

    let startTime = null;
    let animationFrameId = null;
    let lastDisplayedCount = -1;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / actualDuration, 1);
      
      // Apply easing
      const easedProgress = easingFunctions[easing]?.(progress) || progress;
      
      // Calculate current count value
      const currentCount = Math.floor(easedProgress * numericValue);
      
      // Only update state if the count has actually changed
      // This reduces unnecessary re-renders
      if (currentCount !== lastDisplayedCount) {
        setCount(currentCount);
        lastDisplayedCount = currentCount;
      }

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [numericValue, actualDuration, isVisible, easing]);

  // Format the displayed value
  const displayValue = formatNumber ? count.toLocaleString() : count.toString();

  return (
    <div ref={counterRef} className={`hvnly-stat-value ${className}`}>
      {prefix}{displayValue}{suffix}
    </div>
  );
};

export default AnimatedCounter;